/* eslint-disable @typescript-eslint/no-floating-promises */

'use client'

import React, { useState } from 'react'
import { TAP_BTN_EVENT } from '@components/MainWidget/FastagWidget/constants'
import { trackEvent } from '@acko-tech/building-blocks.ui.acko-analytics'
import { fetchWidgetProductFn, widgetJourney } from '@configs/segmentEvents.config'
import { WidgetType } from '@enums/WidgetType'
import CarInsuranceSubmission from '@components/GICar/CarInsuranceSubmission'
import { useGICar } from '@context/GICarContext/GICarContext'
import { useEnvVariablesClientConfig } from '@context/EnvClientContext/EnvVariablesClientContext'
import { getConfig } from '@configs/envConfig'
import PropertyControlledComponent from '@components/PropertyControlledComponent/PropertyControlledComponent'
import { AutoProduct } from '@enums/AutoProduct'
import IllogicalFlowModal from '@components/common/IllogicalFlowModal/illogicalFlowModal'
import { CONSTANTS } from '@components/GICar/config'
import {
  handleCarProposalSubmissionError,
  handleCarProposalSuccess,
} from '@components/GICar/carProposalOutcome'
import { type ChallanListItem } from './DesktopChallanCard'
import NoChallanFound from './NoChallanFound'
import DesktopChallanList from './DesktopChallanList'
import DaysLeftRing from './DaysLeftRing'
import styles from './DesktopChallanPPED.module.scss'

const VERIFIED_BADGE_SRC = 'https://acko-cms.ackoassets.com/SUV_Light_ce285abfd0.webp'

export type { ChallanListItem } from './DesktopChallanCard'

export type DesktopChallanModalPPEDProps = {
  regNum: string
  challanList?: ChallanListItem[]
  expiryDate?: string
  daysLeft?: number
  onSubmit?: () => void
  onViewAllChallans?: () => void
  segmentEventCommonProperties?: any
  widgetType?: string
  vehicleInfoData?: any
  regexPatternsData: any
}

const DesktopChallanModalPPED = ({
  regNum,
  challanList = [],
  expiryDate,
  daysLeft,
  onViewAllChallans,
  segmentEventCommonProperties,
  widgetType = WidgetType.CHALLAN_WIDGET_LANDING,
  vehicleInfoData,
  regexPatternsData
}: DesktopChallanModalPPEDProps) => {
  const hasChallans = challanList.length > 0
  const giCarContext = useGICar()
  const envConfig = useEnvVariablesClientConfig()
  const turnstileConfig = getConfig(envConfig.ACKO_ENV)?.turnstile
  const [showIllogicalModal, setShowIllogicalModal] = useState(false)

  const handleSuccessfulSubmission = (proposalData: any) => {
    handleCarProposalSuccess(proposalData, giCarContext, regNum)
  }

  const handleSubmissionError = (error: unknown) => {
    handleCarProposalSubmissionError(error, giCarContext, setShowIllogicalModal, () => {
      window.location.href = `/t/car?carNumber=${regNum}`
    })
  }

  const handleRetrySubmission = () => {
    giCarContext.resetErrors()
    giCarContext.resetProposalData()
    setShowIllogicalModal(false)
  }

  const handleCheckPrice = async (): Promise<void> => {
    trackEvent(TAP_BTN_EVENT, {
      ...segmentEventCommonProperties,
      journey: widgetJourney?.[widgetType],
      product: fetchWidgetProductFn?.[widgetType]?.(),
      phone: vehicleInfoData?.mobile_number || '',
      registration_number: vehicleInfoData?.reg_number || '',
      cta_text: 'check price',
      action_type: 'redirection',
      redirection_url: '/t/car',
    })
    try {
      await CarInsuranceSubmission(
        vehicleInfoData?.reg_number || '',
        regexPatternsData,
        giCarContext,
        turnstileConfig,
        vehicleInfoData?.mobile_number || '',
        handleSuccessfulSubmission,
        handleSubmissionError
      )
    } catch (error) {
      handleSubmissionError(error)
    }
  }
  return (
    <div className={styles.desktopChallanModalPPED}>
      <div className={styles.leftModalContent}>
        <div className={styles.leftContentInner}>
          <div className={styles.ppedHeader}>
            <div className={styles.ppedHeaderLeft}>
              <div className={styles.ppedTitle}>Your challan details</div>
              <div className={styles.ppedVehicleName}>{vehicleInfoData?.vehicleInfo?.db_make_name} {vehicleInfoData?.vehicleInfo?.db_model_name}</div>
              <div className={styles.numberPlate}>
                <div className={styles.numberPlateBadge}>
                  <svg width="10" height="10" viewBox="0 0 10 10" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <rect x="0.5" y="0.5" width="9" height="9" rx="4.5" stroke="white" strokeLinejoin="round" strokeDasharray="1 2" />
                    <rect x="3" y="3" width="4" height="4" rx="1" fill="url(#paint0_linear_desktop_pped_plate)" />
                    <rect width="2" height="2" transform="translate(4 4)" fill="#E7E7F0" />
                    <defs>
                      <linearGradient id="paint0_linear_desktop_pped_plate" x1="3.5" y1="3" x2="6.5" y2="7" gradientUnits="userSpaceOnUse">
                        <stop stopColor="white" />
                        <stop offset="0.0001" stopColor="white" />
                        <stop offset="0.505" stopColor="#FFF8F8" />
                        <stop offset="1" stopColor="white" />
                      </linearGradient>
                    </defs>
                  </svg>
                  <div className={styles.numberPlateInd}>IND</div>
                </div>
                <div className={styles.numberPlateValue}>{regNum}</div>
              </div>
              {/* <div className={styles.ppedTitle}>Your challan details</div> */}
            </div>
            <img height={80} alt="verified" src={vehicleInfoData?.image || VERIFIED_BADGE_SRC} className={styles.verifiedBadge} />
          </div>

          {hasChallans ? (
            <DesktopChallanList
              challanList={challanList}
              onViewAllChallans={onViewAllChallans}
              segmentEventCommonProperties={segmentEventCommonProperties}
              widgetType={widgetType}
              vehicleInfoData={vehicleInfoData}
            />
          ) : (
            <NoChallanFound />
          )}
        </div>
      </div>

      <div className={styles.rightModalContent}>
        <div className={styles.rightContentInner}>
          <div className={styles.rightTopRow}>
            <div className={styles.rightCopy}>
              <div className={styles.rightHeadline}>
                Check your ACKO insurance
                <br />
                price and save big!
              </div>
              {expiryDate && <div className={styles.rightExpiry}>Your insurance expire{daysLeft && daysLeft > 0 ? 's' : 'd'} on {expiryDate}</div>}
            </div>
            {daysLeft != null && <DaysLeftRing daysLeft={daysLeft} />}
          </div>

          <div className={styles.featureCards}>
            <div className={styles.featureCard}>
              <img src="https://acko-cms.ackoassets.com/zero_commission_icon_8c978c7e09_1_b367727534.svg" />
              <div className={styles.featureCardText}>Commission-free <br /> prices</div>
            </div>
            <div className={styles.featureCard}>
              <img src="https://acko-cms.ackoassets.com/Frame_7eff0dc73b.svg" />
              <div className={styles.featureCardText}>Cashless repairs <br />
                at any garage</div>
            </div>
          </div>

          <button
            type="button"
            className={styles.ppedCta}
            onClick={() => {
              handleCheckPrice()
            }}
          >
            {giCarContext.isLoading && <span className={styles.spinner}></span>}
            Check your price
          </button>
        </div>
      </div>
      <PropertyControlledComponent
        controllerProperty={showIllogicalModal && giCarContext.illogicalFlowData}
        fallback={null}>
        {giCarContext.illogicalFlowData && (
          <IllogicalFlowModal
            errorCode={giCarContext.illogicalFlowData.error_code}
            illogicalFlowData={giCarContext.illogicalFlowData}
            product={AutoProduct.Car as any}
            regNo={giCarContext.illogicalFlowData.registration_number || regNum || ''}
            identifiedVehicleType={giCarContext.illogicalFlowData.vehicle_type || CONSTANTS.DEFAULT_VEHICLE_TYPE}
            showIllogicalFlowModal={showIllogicalModal}
            setShowIllogicalFlowModal={setShowIllogicalModal}
            onClose={handleRetrySubmission}
            secondaryCta={handleRetrySubmission}
            setProposalAPIResponse={giCarContext.setProposalData}
          />
        )}
      </PropertyControlledComponent>
    </div>
  )
}

export default DesktopChallanModalPPED
